/**
 * ReceiptInvoiceMatchView 會計對帳 (S5.1) — receipt-vs-vendor-invoice
 * amount match, the three-way-match PRECURSOR (Board C/7).
 *
 * READ-ONLY referenced surface: the receipt side is owned by 進貨
 * (procurement) and the invoice side by AP (W7/W8) — every row carries a
 * `SourceStampTag` and the only affordance is drill-through to the owning
 * module. Write-back never happens here.
 *
 * Status chips: 金額相符 matched · 金額差異 variance · 未對帳 unmatched (no
 * vendor invoice registered yet — the 未整合 manual-intake pain until W7).
 * Variance renders the signed number, never a colour-only channel.
 *
 * Slice-local seam (S5.1 file-set): fetch is co-located because
 * `src/services/api/**` / `src/hooks/**` belong to concurrent slices this
 * wave — follow-up: lift alongside the GoodsReceiptPage seam.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Dropdown, Input, Table, Tag, Typography, theme } from 'antd';
import type { TableProps } from 'antd';
import {
  ContainerOutlined,
  FilterOutlined,
  MoreOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useRequest } from 'ahooks';
import PageHeader from '@/components/common/PageHeader';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import GenericFilterPopover from '@/components/common/GenericFilterPopover';
import type { FieldConfig, FilterValues } from '@/components/common/GenericFilterPopover';
import { HighlightText } from '@/components/common/HighlightText';
import SourceStampTag from '@/components/common/SourceStampTag';
import apiClient from '@/services/api/axios.config';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import type {
  InvoiceMatchStatus,
  ReceiptInvoiceMatchResponse,
  ReceiptInvoiceMatchRow,
} from '@/types/api/goodsReceipt';
import { INVOICE_MATCH_STATUS_ORDER } from '@/types/api/goodsReceipt';
import { formatMoney } from '../salesOrders/components/soFormat';

const { Text } = Typography;

const MATCH_TAG_COLOR: Record<InvoiceMatchStatus, string> = {
  matched: 'green',
  variance: 'orange',
  unmatched: 'default',
};

interface MatchFilterPopoverValues extends FilterValues {
  status?: string;
}

const ReceiptInvoiceMatchView = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['finance', 'common']);
  const { token } = theme.useToken();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | InvoiceMatchStatus>('all');
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);

  const { data, loading, error, refresh } = useRequest(
    async () => {
      const { data: body } = await apiClient.get<ReceiptInvoiceMatchResponse>(
        '/finance/receipt-invoice-match'
      );
      return body;
    },
    { cacheKey: '/api/finance/receipt-invoice-match' }
  );

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (data?.data ?? []).filter((row) => {
      if (statusFilter !== 'all' && row.status !== statusFilter) return false;
      if (!term) return true;
      return [row.docketNumber, row.poNumber, row.vendorName, row.vendorInvoiceNumber ?? '']
        .join(' ')
        .toLowerCase()
        .includes(term);
    });
  }, [data, search, statusFilter]);

  const isFiltered = search.trim().length > 0 || statusFilter !== 'all';
  const activeFilterCount = statusFilter === 'all' ? 0 : 1;

  const filterFields: FieldConfig[] = useMemo(
    () => [
      {
        key: 'status',
        label: t('finance:match.fields.status'),
        type: 'radio-cards',
        defaultValue: 'all',
        options: [
          { value: 'all', label: t('finance:match.filters.allStatuses') },
          ...INVOICE_MATCH_STATUS_ORDER.map((status) => ({
            value: status,
            label: t(`finance:match.status.${status}`),
          })),
        ],
        resetText: t('common:actions.reset'),
      },
    ],
    [t]
  );

  const columns: TableProps<ReceiptInvoiceMatchRow>['columns'] = [
    {
      title: t('finance:match.fields.docketNumber'),
      dataIndex: 'docketNumber',
      key: 'docketNumber',
      width: 170,
      render: (_: string, record) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <span style={{ fontWeight: token.fontWeightStrong }}>
            <HighlightText text={record.docketNumber} searchTerm={search} fallback="-" />
          </span>
          <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
            {record.receiptDate}
          </span>
        </div>
      ),
    },
    {
      title: t('finance:match.fields.poNumber'),
      dataIndex: 'poNumber',
      key: 'poNumber',
      width: 220,
      render: (_: string, record) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
          <HighlightText text={record.poNumber} searchTerm={search} fallback="-" />
          <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
            <HighlightText text={record.vendorName} searchTerm={search} fallback="" />
          </span>
        </div>
      ),
    },
    {
      title: t('finance:match.fields.receiptAmount'),
      dataIndex: 'receiptAmount',
      key: 'receiptAmount',
      width: 150,
      align: 'right',
      render: (_: number, record) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatMoney(record.currency, record.receiptAmount)}
        </span>
      ),
    },
    {
      title: t('finance:match.fields.invoiceNumber'),
      dataIndex: 'vendorInvoiceNumber',
      key: 'vendorInvoiceNumber',
      width: 180,
      render: (value: string | null) =>
        value ? <HighlightText text={value} searchTerm={search} fallback="-" /> : renderDash(),
    },
    {
      title: t('finance:match.fields.invoiceAmount'),
      dataIndex: 'invoiceAmount',
      key: 'invoiceAmount',
      width: 150,
      align: 'right',
      render: (_: number | null, record) =>
        record.invoiceAmount == null ? (
          renderDash()
        ) : (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatMoney(record.currency, record.invoiceAmount)}
          </span>
        ),
    },
    {
      title: t('finance:match.fields.variance'),
      dataIndex: 'variance',
      key: 'variance',
      width: 130,
      align: 'right',
      render: (_: number | null, record) => {
        if (record.variance == null) return renderDash();
        const signed = `${record.variance > 0 ? '+' : ''}${record.variance.toFixed(2)}`;
        return (
          <span
            style={{
              fontVariantNumeric: 'tabular-nums',
              fontWeight: record.variance !== 0 ? token.fontWeightStrong : undefined,
            }}
          >
            {signed}
          </span>
        );
      },
    },
    {
      title: t('finance:match.fields.status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: InvoiceMatchStatus) => (
        <Tag color={MATCH_TAG_COLOR[status]} style={{ margin: 0 }}>
          {t(`finance:match.status.${status}`)}
        </Tag>
      ),
    },
    {
      title: t('finance:match.fields.source'),
      key: 'source',
      width: 170,
      render: () =>
        data ? (
          <SourceStampTag
            sourceLabel={t('finance:match.sourceReceipts')}
            syncedAt={data.syncedAt}
          />
        ) : (
          renderDash()
        ),
    },
    {
      title: t('finance:match.fields.actions'),
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_: unknown, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'drillReceipts',
                label: t('finance:match.actions.drillReceipts'),
                icon: <ContainerOutlined />,
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  navigate('/auth/goods-receipts');
                },
              },
            ],
          }}
          trigger={['click']}
        >
          <Button
            type="text"
            aria-label={t('finance:match.actions.actionsFor', {
              docketNumber: record.docketNumber,
            })}
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="tw-px-6 tw-pb-8">
      <PageHeader title={t('finance:match.title')} />

      <div className="tw-mb-6 tw-flex tw-gap-3 tw-items-center">
        <Input
          placeholder={t('finance:match.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          variant="outlined"
          size="large"
          suffix={<SearchOutlined style={{ fontSize: token.sizeMD, color: token.colorPrimary }} />}
          style={{
            width: '450px',
            borderRadius: radiusTokens.card,
            height: `${controlHeightTokens.controlHeightXL}px`,
          }}
        />
        <GenericFilterPopover<MatchFilterPopoverValues>
          open={filterPopoverOpen}
          onOpenChange={setFilterPopoverOpen}
          onApply={(values) =>
            setStatusFilter((values.status as 'all' | InvoiceMatchStatus) || 'all')
          }
          initialFilters={{ status: statusFilter }}
          fields={filterFields}
          applyText={t('common:actions.apply')}
          cancelText={t('common:actions.cancel')}
        >
          <Badge count={activeFilterCount}>
            <Button
              aria-label={t('common:actions.filter')}
              icon={<FilterOutlined style={{ color: token.colorPrimary }} />}
              size="large"
              style={{
                borderRadius: radiusTokens.card,
                height: `${controlHeightTokens.controlHeightXL}px`,
                width: `${controlHeightTokens.controlHeightXL}px`,
              }}
            />
          </Badge>
        </GenericFilterPopover>
      </div>

      <TableContainer
        errorState={{
          active: Boolean(error) && !loading,
          title: t('finance:match.states.loadFailed'),
          retryLabel: t('common:errors.boundary.retry'),
          onRetry: refresh,
        }}
      >
        <Table<ReceiptInvoiceMatchRow>
          dataSource={rows}
          columns={columns}
          rowKey="receiptId"
          loading={loading}
          locale={{
            emptyText: isFiltered ? (
              <ListEmptyState
                title={t('finance:match.states.noResults')}
                actionLabel={t('finance:match.states.clearFilters')}
                onAction={() => {
                  setSearch('');
                  setStatusFilter('all');
                }}
                actionType="default"
              />
            ) : (
              <ListEmptyState title={t('finance:match.states.noRows')} />
            ),
          }}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          scroll={{ x: 1370 }}
        />
      </TableContainer>

      {/* Referenced-surface footer — provenance + Tier-1 note (OCC lens precedent) */}
      {data && (
        <Text
          type="secondary"
          style={{ display: 'block', marginTop: '12px', fontSize: token.fontSizeSM }}
        >
          {t('finance:match.footer', {
            time: new Date(data.syncedAt).toLocaleString(),
          })}
        </Text>
      )}
    </div>
  );
};

export default ReceiptInvoiceMatchView;
